import Card from "@mui/material/Card";
import Typography from '@mui/material/Typography';
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TableSearchField from "components/TableSearchField";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Close from "@mui/icons-material/Close";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import RetirosSkeleton from "components/SkeletonLoader/RetirosSkeleton";
import PageBlueHeader from "components/PageBlueHeader";
import PageBlueHeaderSkeleton from "components/PageBlueHeader/PageBlueHeaderSkeleton";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Table from "examples/Tables/Table";
import Cookies from "js-cookie";
import { jsPDF } from "jspdf";
import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { apiRequest } from "utils/api";
import { formatImporteRetiro, monedaRetiro, formatTotalesPorMoneda, agregarLineaConTotal, totalesInicialesDesdeLinea, sumarPorMoneda } from "utils/formatImporteRetiro";
import { useAuth } from "context/AuthContext";
import { useUserData } from "context/UserDataContext";
import { useLanguage } from "context/LanguageContext";

// Función para capitalizar nombres y apellidos (primera letra mayúscula, resto minúscula)
const capitalizeName = (str) => {
  if (!str) return "";
  return str.trim().toLowerCase().split(/\s+/).map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

const PLACEHOLDER_IMAGE_URLS = new Set([
  "http://url.com/imagen.jpg",
  "https://url.com/imagen.jpg",
]);

/** URL de foto de perfil si existe en el objeto usuario (API / Sequelize). */
const resolveProfileImageUrl = (entity) => {
  if (!entity || typeof entity !== "object") return "";
  const keys = [
    "imagen",
    "imagenUrl",
    "imagen_url",
    "foto",
    "fotoPerfil",
    "foto_perfil",
    "avatar",
    "avatarUrl",
    "avatar_url",
    "photoURL",
    "photoUrl",
    "urlImagen",
    "url_imagen",
    "profileImage",
    "picture",
    "image",
  ];
  for (const k of keys) {
    const v = entity[k];
    if (typeof v === "string") {
      const t = v.trim();
      if (!t || PLACEHOLDER_IMAGE_URLS.has(t)) continue;
      return t;
    }
  }
  return "";
};

const inicialesDesdeNombre = (nombreCompleto) => {
  const s = capitalizeName(nombreCompleto);
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.map((w) => w[0]).join("").slice(0, 2).toUpperCase();
};

const inputPaddingSx = {
  "& .MuiOutlinedInput-input": {
    paddingLeft: "0px !important",
    paddingTop: "8px !important",
    paddingBottom: "8px !important",
  },
  "& .MuiSelect-select": {
    paddingLeft: "0px !important",
    paddingTop: "8px !important",
    paddingBottom: "8px !important",
  },
};

/** Mes y año: misma altura (el `sx` del Select va al OutlinedInput raíz; el theme usa `grid` en el slot select). */
const selectMesAnioRetirosSx = (minWidth) => ({
  minWidth,
  minHeight: 40,
  maxHeight: 40,
  boxSizing: "border-box",
  pl: "8px",
  ...inputPaddingSx,
  "& .MuiSelect-select": {
    ...inputPaddingSx["& .MuiSelect-select"],
    minHeight: "0 !important",
    display: "flex !important",
    alignItems: "center",
    lineHeight: 1.25,
  },
});

function Retiros() {
  const navigate = useNavigate();
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [retirosAgrupados, setRetirosAgrupados] = useState([]);
  const [terminoBusqueda, setTerminoBusqueda] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [modalDetalleRetiros, setModalDetalleRetiros] = useState(null);
  const [modalAvatarError, setModalAvatarError] = useState(false);

  useEffect(() => {
    setModalAvatarError(false);
  }, [modalDetalleRetiros]);

  const handleMesChange = (event) => {
    setMesSeleccionado(event.target.value);
  };

  const handleAnioChange = (event) => {
    setAnioSeleccionado(event.target.value);
  };

  const handleBusquedaChange = (event) => {
    setTerminoBusqueda(event.target.value);
  };

  const { user, loading } = useAuth();
  const { userData, empresaData } = useUserData();
  const { t, lang } = useLanguage();



  // Mapa de pasies a timezones
  const COUNTRY_TIMEZONES = {
    'Uruguay': 'America/Montevideo',
    'Argentina': 'America/Argentina/Buenos_Aires',
    'Chile': 'America/Santiago',
    'Colombia': 'America/Bogota',
    'Mexico': 'America/Mexico_City',
    'Peru': 'America/Lima',
    // Fallback default
    'default': 'America/Montevideo'
  };

  const getRegionDate = (country) => {
    const timeZone = COUNTRY_TIMEZONES[country] || COUNTRY_TIMEZONES['default'];
    try {
      const dateString = new Date().toLocaleString("en-US", { timeZone });
      return new Date(dateString);
    } catch (e) {
      console.error("Error getting region date:", e);
      return new Date();
    }
  };

  const fechaActual = getRegionDate(empresaData?.pais);
  const mesActual = fechaActual.getMonth() + 1;
  const anioActual = fechaActual.getFullYear();

  // Función para verificar si una fecha es válida (no posterior a la actual)
  const esFechaValida = (mes, anio) => {
    if (anio < anioActual) return true;
    if (anio > anioActual) return false;
    return mes <= mesActual;
  };




  const obtenerYGuardarUserId = async () => {
    try {
      const response = await apiRequest('/datos-usuario', {
        method: 'GET',
      });

      if (!response.ok) throw new Error('Error al obtener el userId');

      const data = await response.json();
      Cookies.set('userId', data.id); // Guardar el userId en las cookies
    } catch (error) {
      // Error al obtener userId
    }
  };

  const registrarAccion = async (accion, detalles = '') => {
    try {
      const userId = Cookies.get('userId');
      if (!userId) {
        return;
      }

      const bodyData = {
        userId,
        accion,
        detalles,
        origen: 'empresas'
      };

      // Validar que origen no sea null o undefined
      if (!bodyData.origen || bodyData.origen === null) {
        bodyData.origen = 'empresas';
      }

      const jsonBody = JSON.stringify(bodyData);

      const response = await apiRequest('/registrar-accion', {
        method: 'POST',
        body: jsonBody
      });

      if (!response.ok) throw new Error('Error al registrar la acción');
    } catch (error) {
      // Error al registrar acción
    }
  };

  const fetchHistorialRetiros = async () => {
    console.log('[Retiros] fetchHistorialRetiros iniciado');
    setLoadingData(true);
    const empresaId = empresaData?.id || userData?.empresaId;
    console.log('[Retiros] empresaId:', empresaId, 'mes:', mesSeleccionado, 'anio:', anioSeleccionado);

    if (!empresaId) {
      console.log('[Retiros] No hay empresaId, saliendo');
      setLoadingData(false);
      return;
    }

    const urlRetiros = `/retiros-empresa?empresaId=${empresaId}&mes=${mesSeleccionado}&anio=${anioSeleccionado}`;

    try {
      // Obtener retiros y usuarios en paralelo
      const [retirosResponse, usuariosResponse] = await Promise.all([
        apiRequest(urlRetiros),
        apiRequest(`/usuarios-empresa/${empresaId}`)
      ]);

      if (retirosResponse.ok && usuariosResponse.ok) {
        const retiros = await retirosResponse.json();
        const usuarios = await usuariosResponse.json();
        console.log('[Retiros] API respondió - retiros:', retiros.length, 'usuarios:', usuarios.length);
        console.log('[Retiros] Primer retiro:', retiros[0]);

        // Verificar si los retiros ya tienen la información COMPLETA del usuario
        const tieneUserCompleto = retiros.length > 0 &&
          retiros[0].User &&
          retiros[0].User.nombre &&
          retiros[0].User.apellido &&
          retiros[0].User.documento;

        if (tieneUserCompleto) {
          // Si los retiros tienen User completo, usar esa información directamente
          console.log('[Retiros] Usando User incluido en retiros');
          agruparRetiros(retiros, []);
          return;
        }

        console.log('[Retiros] User no completo, usando lista de usuarios');

        // Obtener userIds únicos de los retiros
        const userIdsRetiros = [...new Set(retiros.map(r => r.userId))];

        // Crear mapa de usuarios existentes
        const usuariosMap = usuarios.reduce((acc, usuario) => {
          if (usuario.id) {
            acc[usuario.id] = usuario;
          }
          return acc;
        }, {});

        // Encontrar userIds que no están en la lista de usuarios
        const userIdsFaltantes = userIdsRetiros.filter(userId => !usuariosMap[userId]);


        // Obtener usuarios faltantes usando el endpoint correcto
        if (userIdsFaltantes.length > 0) {
          const usuariosFaltantesPromises = userIdsFaltantes.map(async (userId) => {
            try {
              const response = await apiRequest(`/datos-usuario-id/${userId}`);
              if (response.ok) {
                const usuario = await response.json();
                return usuario;
              }
              return null;
            } catch (error) {
              return null;
            }
          });

          const usuariosFaltantes = (await Promise.all(usuariosFaltantesPromises)).filter(u => u !== null);

          // Agregar usuarios faltantes al mapa
          usuariosFaltantes.forEach(usuario => {
            if (usuario.id) {
              usuariosMap[usuario.id] = usuario;
            }
          });
        }

        // Convertir el mapa de vuelta a array para compatibilidad
        const todosLosUsuarios = Object.values(usuariosMap);

        agruparRetiros(retiros, todosLosUsuarios);
      } else {
        throw new Error('Failed to fetch historial data');
      }
    } catch (error) {
      // Error fetching withdrawal history
    } finally {
      setLoadingData(false);
    }
  };

  const agruparRetiros = (retiros, usuarios) => {
    console.log('[Retiros] agruparRetiros - retiros:', retiros?.length, 'usuarios:', usuarios?.length);
    // Verificar si retiros es un array
    if (!Array.isArray(retiros)) {
      console.log('[Retiros] retiros no es array');
      setRetirosAgrupados([]);
      return;
    }

    // Verificar si los retiros tienen información COMPLETA del usuario
    const tieneUserCompleto = retiros.length > 0 &&
      retiros[0].User &&
      retiros[0].User.nombre &&
      retiros[0].User.apellido &&
      retiros[0].User.documento;

    // Si los retiros ya tienen la información del usuario (User), usarla directamente
    if (tieneUserCompleto) {
      console.log('[Retiros] Usando datos de User incluido');
      const retirosFiltrados = retiros.filter(item =>
        item.suscripcion === false &&
        item.User &&
        item.User.nombre &&
        item.User.apellido &&
        item.User.documento
      );

      const resultado = retirosFiltrados.reduce((acc, item) => {
        const found = acc.find(a => a.userId === item.userId);
        const linea = { fecha: item.fecha, cantidad: item.cantidad, moneda: monedaRetiro(item) };
        if (found) {
          agregarLineaConTotal(found, linea);
        } else {
          acc.push({
            userId: item.userId,
            nombre: `${item.User.nombre} ${item.User.apellido}`,
            documento: item.User.documento,
            detalle: [linea],
            totalesPorMoneda: totalesInicialesDesdeLinea(linea),
            fotoUrl: resolveProfileImageUrl(item.User),
          });
        }
        return acc;
      }, []);

      console.log('[Retiros] Resultado agrupado (con User):', resultado.length, resultado);
      setRetirosAgrupados(resultado);
      return;
    }

    // Si no, usar el mapa de usuarios
    // Verificar si usuarios es un array
    if (!Array.isArray(usuarios)) {
      setRetirosAgrupados([]);
      return;
    }

    // Crear un mapa de userId -> usuario para búsqueda rápida
    const usuariosMap = usuarios.reduce((acc, usuario) => {
      if (usuario.id) {
        acc[usuario.id] = usuario;
      }
      return acc;
    }, {});


    // Obtener todos los userIds únicos de los retiros
    const userIdsRetiros = [...new Set(retiros.map(r => r.userId))];

    // Obtener todos los ids de los usuarios
    const idsUsuarios = usuarios.map(u => u.id);

    // Verificar si hay coincidencias
    const coincidencias = userIdsRetiros.filter(userId => idsUsuarios.includes(userId));

    // Filtrar solo retiros donde suscripcion es false y existe el usuario en el mapa
    const retirosFiltrados = retiros.filter(item => {
      const usuario = usuariosMap[item.userId];
      const pasaFiltro = item.suscripcion === false &&
        usuario &&
        usuario.nombre &&
        usuario.apellido &&
        usuario.documento;

      return pasaFiltro;
    });

    // Agrupar retiros por usuario
    const resultado = retirosFiltrados.reduce((acc, item) => {
      const usuario = usuariosMap[item.userId];
      const found = acc.find(a => a.userId === item.userId);
      const linea = { fecha: item.fecha, cantidad: item.cantidad, moneda: monedaRetiro(item) };
      if (found) {
        agregarLineaConTotal(found, linea);
      } else {
        acc.push({
          userId: item.userId,
          nombre: `${usuario.nombre} ${usuario.apellido}`,
          documento: usuario.documento,
          detalle: [linea],
          totalesPorMoneda: totalesInicialesDesdeLinea(linea),
          fotoUrl: resolveProfileImageUrl(usuario),
        });
      }
      return acc;
    }, []);

    console.log('[Retiros] Resultado agrupado (sin User):', resultado.length, resultado);
    setRetirosAgrupados(resultado);
  };

  // useEffect para cargar datos - debe estar después de las definiciones de funciones
  useEffect(() => {
    // Si Firebase está cargando, esperar
    if (loading) {
      return;
    }

    // Si no hay usuario autenticado, redirigir al login
    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }

    // Esperar a que los datos de empresa estén disponibles
    const empresaId = empresaData?.id || userData?.empresaId;
    if (!empresaId) {
      return;
    }

    // Si hay usuario y empresa, cargar datos
    fetchHistorialRetiros();
    obtenerYGuardarUserId();
  }, [navigate, mesSeleccionado, anioSeleccionado, user, loading, empresaData, userData]);


  const generarHistorialPDF = async () => {
    const doc = new jsPDF();
    const imgData = "/favicon.png";
    const mesNombre = t(`month.${mesSeleccionado}`);
    const empresaId = empresaData?.id || userData?.empresaId;
    if (!empresaId) {
      return;
    }

    try {
      // Obtener retiros y usuarios en paralelo
      const [retirosResponse, usuariosResponse] = await Promise.all([
        apiRequest(`/retiros-empresa?empresaId=${empresaId}&mes=${mesSeleccionado}&anio=${anioSeleccionado}`),
        apiRequest(`/usuarios-empresa/${empresaId}`)
      ]);

      const retiros = await retirosResponse.json();
      const usuarios = await usuariosResponse.json();

      // Crear un mapa de userId -> usuario
      const usuariosMap = usuarios.reduce((acc, usuario) => {
        if (usuario.id) {
          acc[usuario.id] = usuario;
        }
        return acc;
      }, {});

      // Filtrar solo retiros donde suscripcion es false y existe el usuario
      const retirosFiltrados = retiros.filter(retiro => {
        const usuario = usuariosMap[retiro.userId];
        return retiro.suscripcion === false &&
          usuario &&
          usuario.nombre &&
          usuario.apellido &&
          usuario.documento;
      });

      if (retirosFiltrados.length === 0) {
        doc.text(t('pdf.retiros.noData'), 10, 40);
      } else {
        const retirosAgrupados = retirosFiltrados.reduce((acc, retiro) => {
          const { userId } = retiro;
          const usuario = usuariosMap[userId];
          if (!acc[userId] && usuario) {
            acc[userId] = { ...usuario, retiros: [] };
          }
          if (usuario) {
            acc[userId].retiros.push(retiro);
          }
          return acc;
        }, {});

        let isFirstPage = true;

        for (const usuarioId in retirosAgrupados) {
          const usuario = retirosAgrupados[usuarioId];
          const totalRetirado = sumarPorMoneda(usuario.retiros, {
            cantidadKey: "cantidad",
            getMoneda: monedaRetiro,
          });

          if (!isFirstPage) {
            doc.addPage();
          }
          isFirstPage = false;

          // Header en cada página
          doc.addImage(imgData, 'PNG', 10, 10, 30, 30);
          doc.setTextColor(40, 40, 40);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(24);
          doc.text(t('pdf.retiros.title', { month: mesNombre }), 50, 20);
          doc.setFontSize(12);
          doc.text(t('pdf.retiros.support'), 50, 30);

          let y = 45;
          doc.setFontSize(16);
          doc.text(`${usuario.nombre} ${usuario.apellido}`, 10, y);
          doc.setFontSize(12);
          doc.text(`${t('pdf.retiros.document')}: ${usuario.documento}`, 10, y + 10);
          doc.text(
            `${t('pdf.retiros.totalWithdrawn')}: ${formatTotalesPorMoneda(totalRetirado)}`,
            10,
            y + 20
          );
          y += 35;

          // Tabla de detalles de retiros
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.setFillColor(230, 230, 230);
          doc.rect(10, y, 190, 8, 'F');
          doc.text(t('pdf.retiros.date'), 15, y + 5);
          doc.text(t('pdf.retiros.hour'), 65, y + 5);
          doc.text(t('pdf.retiros.amount'), 150, y + 5);
          y += 13;

          for (const retiro of usuario.retiros) {
            const fechaHora = new Date(retiro.fecha);
            const fecha = fechaHora.toLocaleDateString();
            const hora = fechaHora.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const importe = Math.round(retiro.cantidad);

            doc.text(fecha, 15, y);
            doc.text(hora, 65, y);
            doc.text(formatImporteRetiro(importe, monedaRetiro(retiro)), 150, y);
            y += 10;

            if (y > 270) {
              doc.addPage();
              doc.addImage(imgData, 'PNG', 10, 10, 30, 30);
              doc.setFontSize(24);
              doc.text(t('pdf.retiros.title', { month: mesNombre }), 50, 20);
              doc.setFontSize(12);
              doc.text(t('pdf.retiros.support'), 50, 30);
              y = 50;

              doc.setFillColor(230, 230, 230);
              doc.rect(10, y, 190, 8, 'F');
              doc.text(t('pdf.retiros.date'), 15, y + 5);
              doc.text(t('pdf.retiros.hour'), 65, y + 5);
              doc.text(t('pdf.retiros.amount'), 150, y + 5);
              y += 13;
            }
          }
        }
      }

      // Nueva página para cuentas bancarias
      doc.addPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text("Información de Cuentas Bancarias para Depósitos", 105, 20, null, null, 'center');

      // Logo e información de Itaú
      const itauLogoUrl = "https://upload.wikimedia.org/wikipedia/commons/thumb/1/19/Itaú_Unibanco_logo_2023.svg/800px-Itaú_Unibanco_logo_2023.svg.png";
      const santanderLogoUrl = "https://specto.com.br/wp-content/uploads/2018/02/logo-specto-case-santander-01.png";

      // Insertar logo y detalles para Itaú
      doc.addImage(itauLogoUrl, 'PNG', 10, 40, 25, 25);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text("Banco: Itaú", 50, 45);
      doc.setFont('helvetica', 'normal');
      doc.text("Número de cuenta: 1976430", 50, 55);
      doc.text("Destinatario: PayFlex SAS", 50, 65);
      doc.text("Moneda: UYU", 50, 75);

      // Línea de separación
      doc.setDrawColor(150);
      doc.setLineWidth(0.5);
      doc.line(10, 85, 200, 85);

      // Insertar logo y detalles para Santander
      doc.addImage(santanderLogoUrl, 'PNG', 10, 95, 25, 25);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text("Banco: Santander", 50, 100);
      doc.setFont('helvetica', 'normal');
      doc.text("Transferencias dentro de Santander", 50, 110);
      doc.text("Cuenta: 1479920", 50, 120);
      doc.text("Destinatario: PAYFLEX SAS", 50, 130);
      doc.text("Moneda: UYU", 50, 140);
      doc.text("Sucursal: 84 - Biarritz", 50, 150);

      doc.setFont('helvetica', 'bold');
      doc.text("Transferencias desde otros bancos", 50, 165);
      doc.setFont('helvetica', 'normal');
      doc.text("Cuenta: 0084000001479920", 50, 175);
      doc.text("Moneda: UYU", 50, 185);

      doc.save(`PayFlex-Historial-Retiros-${mesNombre}-${anioSeleccionado}.pdf`);

      await registrarAccion('Descargar PDF', `Comprobante de ${mesNombre} descargado`);

    } catch (error) {
      doc.save(`PayFlex-Historial-Retiros-${mesNombre}-${anioSeleccionado}.pdf`);
    }
  };



  const nombresMeses = [1,2,3,4,5,6,7,8,9,10,11,12].map((n) => t(`month.${n}`));

  const esFechaActual = mesSeleccionado === mesActual && anioSeleccionado === anioActual;
  const mesNombre = nombresMeses[mesSeleccionado - 1];

  // Filtrar retiros basándose en el término de búsqueda
  const retirosFiltrados = retirosAgrupados.filter(retiro => {
    if (!terminoBusqueda) return true;
    const busqueda = terminoBusqueda.toLowerCase();
    return (
      retiro.nombre.toLowerCase().includes(busqueda) ||
      retiro.documento.toLowerCase().includes(busqueda)
    );
  });


  const columns = [
    { name: "nombre", align: "center", label: t('withdrawals.table.name') },
    { name: "documento", align: "center", label: t('withdrawals.table.document') },
    { name: "cantidad", align: "center", label: t('withdrawals.table.amount') },
    { name: "acciones", align: "center", label: t('withdrawals.table.movements') },
  ];

  const rows = retirosFiltrados.map(retiro => ({
    nombre: (
      <ArgonTypography variant="button" textTransform="none" fontWeight="regular" color="#000000">
        {capitalizeName(retiro.nombre)}
      </ArgonTypography>
    ),
    documento: retiro.documento,
    cantidad: (
      <Typography
        variant="body2"
        style={{ fontWeight: "600", whiteSpace: "pre-line", textAlign: "center" }}
      >
        {formatTotalesPorMoneda(retiro.totalesPorMoneda, { separator: "\n" })}
      </Typography>
    ),
    acciones: (
      <ArgonButton
        size="small"
        variant="outlined"
        color="dark"
        startIcon={<VisibilityOutlined sx={{ fontSize: "1.05rem !important", opacity: 0.85 }} />}
        sx={{
          minWidth: 132,
          py: 0.5,
          px: 1.5,
          fontWeight: 600,
          fontSize: "0.8125rem",
          textTransform: "none",
          borderColor: "rgba(15, 23, 42, 0.12)",
          color: "#0f172a",
          bgcolor: "rgba(255,255,255,0.9)",
          "&:hover": {
            borderColor: "rgba(15, 23, 42, 0.22)",
            bgcolor: "#f8fafc",
            color: "#0f172a",
          },
        }}
        onClick={() =>
          setModalDetalleRetiros({
            nombre: retiro.nombre,
            documento: retiro.documento,
            fotoUrl: retiro.fotoUrl || "",
            detalle: [...(retiro.detalle || [])].sort(
              (a, b) => new Date(a.fecha) - new Date(b.fecha)
            ),
            totalesPorMoneda: retiro.totalesPorMoneda,
          })
        }
      >
        {t('withdrawals.viewBtn')}
      </ArgonButton>
    )
  }));

  const withdrawalsPageTitle = esFechaActual
    ? t('withdrawals.titleThisMonth')
    : t('withdrawals.titleOf', { month: mesNombre });

  return (
    <DashboardLayout>
      {loadingData ? (
        <PageBlueHeaderSkeleton />
      ) : (
        <PageBlueHeader
          title={withdrawalsPageTitle}
          subtitle={t('withdrawals.pageSubtitle')}
        />
      )}
      <ArgonBox pt={2} pb={6} sx={{ "& thead th": { fontSize: '0.9rem', fontWeight: 700 } }}>
        <Card>
          <ArgonBox display="flex" justifyContent="flex-start" alignItems="center" p={3}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <TableSearchField
                placeholder={t('withdrawals.search')}
                value={terminoBusqueda}
                onChange={handleBusquedaChange}
              />
              <Select
                value={mesSeleccionado}
                onChange={handleMesChange}
                displayEmpty
                variant="outlined"
                size="small"
                sx={selectMesAnioRetirosSx(120)}
              >
                {nombresMeses.map((mes, index) => {
                  const mesNumero = index + 1;
                  const esValido = esFechaValida(mesNumero, anioSeleccionado);
                  return (
                    <MenuItem
                      key={mesNumero}
                      value={mesNumero}
                      disabled={!esValido}
                    >
                      {mes}
                    </MenuItem>
                  );
                })}
              </Select>
              <Select
                value={anioSeleccionado}
                onChange={handleAnioChange}
                displayEmpty
                variant="outlined"
                size="small"
                sx={selectMesAnioRetirosSx(100)}
              >
                {(() => {
                  const startYear = 2024;
                  const years = [];
                  for (let i = startYear; i <= anioActual; i++) {
                    years.push(i);
                  }

                  return years.map((anio) => {
                    const esValido = anio <= anioActual;
                    return (
                      <MenuItem
                        key={anio}
                        value={anio}
                        disabled={!esValido}
                      >
                        {anio}
                      </MenuItem>
                    );
                  });
                })()}
              </Select>
            </div>
          </ArgonBox>
          {loadingData ? (
            <RetirosSkeleton />
          ) : rows.length > 0 ? (
            <Table columns={columns} rows={rows} />
          ) : (
            <ArgonBox px={3} pb={3} textAlign="center">
              <Typography variant="body1" sx={{ padding: '24px 0' }}>
                {t('withdrawals.noData')}
              </Typography>
            </ArgonBox>
          )}
        </Card>
      </ArgonBox >
      <Dialog
        open={Boolean(modalDetalleRetiros)}
        onClose={() => setModalDetalleRetiros(null)}
        maxWidth={false}
        scroll="paper"
        PaperProps={{
          elevation: 0,
          sx: {
            width: "100%",
            maxWidth: 440,
            mx: 2,
            borderRadius: "20px",
            overflow: "hidden",
            bgcolor: "#ffffff",
            boxShadow:
              "0 0 0 1px rgba(15, 23, 42, 0.06), 0 24px 48px -12px rgba(15, 23, 42, 0.18), 0 12px 24px -8px rgba(15, 23, 42, 0.08)",
          },
        }}
      >
        {modalDetalleRetiros && (
          <>
            <ArgonBox
              sx={{
                px: 3,
                pt: 3,
                pb: 2,
                position: "relative",
                bgcolor: "#fafafa",
                backgroundImage:
                  "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
              }}
            >
              <IconButton
                aria-label="cerrar"
                onClick={() => setModalDetalleRetiros(null)}
                size="small"
                sx={{
                  position: "absolute",
                  right: 12,
                  top: 12,
                  color: "#94a3b8",
                  "&:hover": { color: "#0f172a", bgcolor: "rgba(15, 23, 42, 0.06)" },
                }}
              >
                <Close sx={{ fontSize: 20 }} />
              </IconButton>

              <ArgonBox sx={{ pr: "32px", width: "100%", boxSizing: "border-box" }}>
                <Typography
                  sx={{
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "#64748b",
                    mb: 1,
                  }}
                >
                  {t('withdrawals.modal.section')}
                </Typography>
                <ArgonBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    columnGap: 1.25,
                    rowGap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.375rem",
                      fontWeight: 600,
                      letterSpacing: "-0.02em",
                      color: "#0f172a",
                      lineHeight: 1.25,
                      fontFamily: "inherit",
                    }}
                  >
                    {t('withdrawals.modal.title')}
                  </Typography>
                  <Chip
                    label={`${mesNombre} ${anioSeleccionado}`}
                    size="small"
                    sx={{
                      height: 28,
                      flexShrink: 0,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      bgcolor: "#f1f5f9",
                      color: "#475569",
                      border: "none",
                      "& .MuiChip-label": { px: 1.25 },
                    }}
                  />
                </ArgonBox>
                <Typography sx={{ fontSize: "0.8125rem", color: "#94a3b8", mt: 1.25 }}>
                  {modalDetalleRetiros.detalle.length}{" "}
                  {modalDetalleRetiros.detalle.length === 1 ? t('withdrawals.modal.movement') : t('withdrawals.modal.movements')}
                </Typography>
              </ArgonBox>
            </ArgonBox>

            <Divider sx={{ borderColor: "rgba(15, 23, 42, 0.06)" }} />

            <DialogContent sx={{ p: 0 }}>
              <ArgonBox sx={{ px: 3, pt: 2.5, pb: 2 }}>
                <ArgonBox sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {modalDetalleRetiros.fotoUrl && !modalAvatarError ? (
                    <ArgonBox
                      component="img"
                      src={modalDetalleRetiros.fotoUrl}
                      alt={capitalizeName(modalDetalleRetiros.nombre)}
                      onError={() => setModalAvatarError(true)}
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "14px",
                        objectFit: "cover",
                        flexShrink: 0,
                        display: "block",
                        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.12)",
                      }}
                    />
                  ) : (
                    <ArgonBox
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: "0.9375rem",
                        letterSpacing: "-0.02em",
                        color: "#fff",
                        flexShrink: 0,
                        background: "linear-gradient(145deg, #334155 0%, #0f172a 100%)",
                        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.2)",
                      }}
                    >
                      {inicialesDesdeNombre(modalDetalleRetiros.nombre)}
                    </ArgonBox>
                  )}
                  <ArgonBox sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "1.0625rem",
                        fontWeight: 600,
                        letterSpacing: "-0.02em",
                        color: "#0f172a",
                        lineHeight: 1.3,
                      }}
                    >
                      {capitalizeName(modalDetalleRetiros.nombre)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.875rem", color: "#64748b", mt: 0.35 }}>
                      Doc. {modalDetalleRetiros.documento}
                    </Typography>
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>

              <ArgonBox sx={{ px: 3, pb: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#94a3b8",
                    mb: 0.5,
                  }}
                >
                  {t('withdrawals.modal.movementsLabel')}
                </Typography>
              </ArgonBox>

              <ArgonBox sx={{ px: 3, pb: 2 }}>
                {modalDetalleRetiros.detalle.map((linea, idx) => {
                  const fechaHora = linea.fecha ? new Date(linea.fecha) : null;
                  const ok = fechaHora && !Number.isNaN(fechaHora.getTime());
                  const dateLocale = lang === 'en' ? 'en-US' : 'es-UY';
                  const fechaStr = ok
                    ? fechaHora.toLocaleDateString(dateLocale, {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                    : "—";
                  const horaStr = ok
                    ? fechaHora.toLocaleTimeString(dateLocale, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                    : "";
                  return (
                    <React.Fragment key={`${linea.fecha}-${idx}`}>
                      {idx > 0 && (
                        <Divider sx={{ borderColor: "rgba(15, 23, 42, 0.06)", my: 0 }} />
                      )}
                      <ArgonBox
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1.75,
                          gap: 2,
                        }}
                      >
                        <ArgonBox sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "0.9375rem",
                              fontWeight: 500,
                              color: "#0f172a",
                              textTransform: "capitalize",
                            }}
                          >
                            {fechaStr}
                          </Typography>
                          {horaStr && (
                            <Typography sx={{ fontSize: "0.8125rem", color: "#94a3b8", mt: 0.25 }}>
                              {horaStr}
                            </Typography>
                          )}
                        </ArgonBox>
                        <Typography
                          sx={{
                            fontSize: "1rem",
                            fontWeight: 600,
                            color: "#0f172a",
                            fontVariantNumeric: "tabular-nums",
                            flexShrink: 0,
                          }}
                        >
                          {formatImporteRetiro(linea.cantidad, linea.moneda || modalDetalleRetiros.moneda)}
                        </Typography>
                      </ArgonBox>
                    </React.Fragment>
                  );
                })}
              </ArgonBox>

              <ArgonBox
                sx={{
                  mx: 3,
                  mb: 2,
                  px: 2.25,
                  py: 2,
                  borderRadius: "14px",
                  bgcolor: "#f8fafc",
                  border: "1px solid rgba(15, 23, 42, 0.06)",
                }}
              >
                <ArgonBox
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 2,
                  }}
                >
                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#64748b" }}>
                    {t('withdrawals.modal.totalMonth')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.25rem",
                      fontWeight: 700,
                      letterSpacing: "-0.03em",
                      color: "#0f172a",
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "pre-line",
                      textAlign: "right",
                    }}
                  >
                    {formatTotalesPorMoneda(modalDetalleRetiros.totalesPorMoneda, { separator: "\n" })}
                  </Typography>
                </ArgonBox>
              </ArgonBox>
            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                py: 2,
                pt: 0,
                bgcolor: "#fafafa",
                borderTop: "1px solid rgba(15, 23, 42, 0.06)",
                justifyContent: "stretch",
              }}
            >
              <ArgonButton
                fullWidth
                variant="gradient"
                color="info"
                onClick={() => setModalDetalleRetiros(null)}
                sx={{
                  py: 1.25,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.9375rem",
                }}
              >
                {t('common.done')}
              </ArgonButton>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Footer />
    </DashboardLayout >
  );
}

export default Retiros;
